import { Fragment, useEffect, useMemo, useState } from "react";
import { MoreHorizontal, Plus, SquareArrowOutUpRight } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import type { PartialSkill, SkillScope } from "~/services/skills";
import { sortSkills } from "./skillSort";

interface SkillsListProps {
  skills: PartialSkill[];
  // Filters the list by name or description (case-insensitive). Supplied by the
  // toolbar search box or by the text typed after "/" in the composer.
  searchFilter?: string;
  // Invoked with the bare skill name (no slash) when a skill is chosen.
  onSelectSkill: (skillName: string) => void;
  onAddSkill?: () => void;
  onEditSkill?: (skill: PartialSkill) => void;
  // Deletes (USER) or detaches (AGENT/DATASET) a skill. Only offered for those
  // scopes; the menu is hidden for SYSTEM skills, which can't be removed here.
  onDeleteSkill?: (skill: PartialSkill) => void;
  onLearnMore?: () => void;
  // Drives arrow-key navigation and Enter/Tab selection via a window listener.
  // Used by the "/" overlay; the toolbar relies on click/search instead.
  enableKeyboardNav?: boolean;
  onClose?: () => void;
}

function filterSkills(skills: PartialSkill[], searchText: string): PartialSkill[] {
  const search = searchText.trim().toLowerCase();
  if (!search) return skills;
  return skills.filter(
    (skill) =>
      skill.name.toLowerCase().includes(search) || skill.description.toLowerCase().includes(search)
  );
}

// Section heading shown above each scope group. Skills arrive pre-sorted by
// scope (see sortSkills), so equal-scope skills are always contiguous.
function sectionLabel(scope: SkillScope): string {
  switch (scope) {
    case "SYSTEM":
      return "Built-in";
    case "AGENT":
      return "From this agent";
    case "DATASET":
      return "From dataset";
    case "USER":
      return "Your skills";
    default:
      return scope;
  }
}

// Scopes the user can change from this surface: USER skills are owned (edit /
// delete), AGENT and DATASET skills can be edited or detached from their
// context. SYSTEM (and other) skills are read-only here, so their actions
// button is shown disabled with an explanatory tooltip rather than hidden.
const MUTABLE_SCOPES: ReadonlySet<SkillScope> = new Set(["USER", "AGENT", "DATASET"]);

const READ_ONLY_ACTIONS_HINT = "Built-in skills can't be edited or removed.";

type NavItem = { kind: "skill"; skill: PartialSkill } | { kind: "add" } | { kind: "help" };

export const SkillsList = ({
  skills,
  searchFilter = "",
  onSelectSkill,
  onAddSkill,
  onEditSkill,
  onDeleteSkill,
  onLearnMore,
  enableKeyboardNav = false,
  onClose,
}: SkillsListProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const filteredSkills = useMemo(
    () => sortSkills(filterSkills(skills, searchFilter)),
    [skills, searchFilter]
  );

  const navItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = filteredSkills.map((skill) => ({ kind: "skill", skill }));
    if (onAddSkill) items.push({ kind: "add" });
    if (onLearnMore) items.push({ kind: "help" });
    return items;
  }, [filteredSkills, onAddSkill, onLearnMore]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [navItems.length]);

  useEffect(() => {
    if (!enableKeyboardNav || navItems.length === 0) return;

    const activate = (item: NavItem) => {
      if (item.kind === "skill") {
        onSelectSkill(item.skill.name);
      } else if (item.kind === "add") {
        onAddSkill?.();
        onClose?.();
      } else {
        onLearnMore?.();
        onClose?.();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % navItems.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + navItems.length) % navItems.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        const item = navItems[selectedIndex];
        if (item) activate(item);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enableKeyboardNav, navItems, selectedIndex, onSelectSkill, onAddSkill, onLearnMore, onClose]);

  const hasFooter = Boolean(onAddSkill || onLearnMore);
  // Footer indices follow the skills in navItems, in render order.
  const addIndex = filteredSkills.length;
  const helpIndex = filteredSkills.length + (onAddSkill ? 1 : 0);

  return (
    <div className="flex max-h-[425px] flex-col overflow-y-auto pb-1" role="list">
      {filteredSkills.length > 0 ? (
        filteredSkills.map((skill, index) => {
          const startsSection = index === 0 || filteredSkills[index - 1].scope !== skill.scope;
          const isMutable = MUTABLE_SCOPES.has(skill.scope);
          const hasActions = Boolean(onEditSkill || onDeleteSkill);
          return (
            <Fragment key={skill.id}>
              {startsSection && (
                <p className="sticky top-0 z-10 border-b border-border bg-popover px-3 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {sectionLabel(skill.scope)}
                </p>
              )}
              <div role="listitem" className="group relative">
                <button
                  type="button"
                  onClick={() => onSelectSkill(skill.name)}
                  className={cn(
                    "flex w-full cursor-pointer flex-col items-start gap-0.5 px-3 py-1.5 pr-9 text-left",
                    "hover:bg-muted focus-visible:bg-muted focus-visible:outline-none",
                    enableKeyboardNav && selectedIndex === index && "bg-muted"
                  )}
                >
                  <span className="font-mono text-xs font-medium text-foreground">
                    /{skill.name}
                  </span>
                  <span className="line-clamp-2 text-xs text-muted-foreground">
                    {skill.description}
                  </span>
                </button>

                {hasActions &&
                  (isMutable ? (
                    // modal={false} so the menu coexists with the parent Popover /
                    // composer overlay focus scope instead of being closed by it.
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(
                            "absolute right-2 top-1/2 size-6 -translate-y-1/2 text-muted-foreground",
                            "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                          )}
                          aria-label={`Actions for ${skill.name}`}
                        >
                          <MoreHorizontal className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-32">
                        {onEditSkill && (
                          <DropdownMenuItem
                            onClick={() => {
                              onEditSkill(skill);
                              onClose?.();
                            }}
                          >
                            Edit
                          </DropdownMenuItem>
                        )}
                        {onDeleteSkill && (
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={() => {
                              onDeleteSkill(skill);
                              onClose?.();
                            }}
                          >
                            {skill.scope === "USER" ? "Delete" : "Detach"}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    // Read-only scopes (SYSTEM): keep the affordance visible but
                    // disabled, with a tooltip explaining why. aria-disabled (not
                    // the native disabled attribute) keeps the trigger hoverable
                    // so the tooltip still fires.
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-disabled
                          onClick={(e) => e.preventDefault()}
                          className={cn(
                            "absolute right-2 top-1/2 size-6 -translate-y-1/2 cursor-not-allowed text-muted-foreground/50",
                            "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                          )}
                          aria-label={`Actions for ${skill.name}`}
                        >
                          <MoreHorizontal className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{READ_ONLY_ACTIONS_HINT}</TooltipContent>
                    </Tooltip>
                  ))}
              </div>
            </Fragment>
          );
        })
      ) : (
        <p className="px-3 py-6 text-center text-xs text-muted-foreground">
          {skills.length === 0
            ? "No skills available for this conversation."
            : `No skills match “${searchFilter}”`}
        </p>
      )}

      {hasFooter && (
        <>
          {filteredSkills.length > 0 && <div className="my-1 border-t border-border" />}
          {onAddSkill && (
            <button
              type="button"
              onClick={() => {
                onAddSkill();
                onClose?.();
              }}
              className={cn(
                "flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-foreground",
                "hover:bg-muted focus-visible:bg-muted focus-visible:outline-none",
                enableKeyboardNav && selectedIndex === addIndex && "bg-muted"
              )}
            >
              <Plus className="size-3.5 text-muted-foreground" />
              Add New Skill…
            </button>
          )}
          {onLearnMore && (
            <button
              type="button"
              onClick={() => {
                onLearnMore();
                onClose?.();
              }}
              className={cn(
                "flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-foreground",
                "hover:bg-muted focus-visible:bg-muted focus-visible:outline-none",
                enableKeyboardNav && selectedIndex === helpIndex && "bg-muted"
              )}
            >
              <SquareArrowOutUpRight className="size-3.5 text-muted-foreground" />
              Learn about AI Skills
            </button>
          )}
        </>
      )}
    </div>
  );
};
